import { memo, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import "./AIComposerCard.css";

const VALID_INTENTS = new Set(["relax", "rest", "recenter"]);
const AURIA_CONTEXT_MESSAGES = Object.freeze({
  relax: "On crée un moment pour relâcher la pression ?",
  rest: "On crée un moment pour ralentir et vous reposer ?",
  recenter: "On crée un moment pour retrouver votre clarté ?",
});

const INTENT_CONFIG = {
  relax: { tempo: 0.85, breathRate: 0.60, breathDepth: 7.2, rMembrane: 76, rCore: 54, coreBreath: 4.6, wobbleM: 1.7, wobbleC: 1.35, open: 3.2 },
  rest: { tempo: 0.56, breathRate: 0.44, breathDepth: 8.4, rMembrane: 78, rCore: 54, coreBreath: 5.4, wobbleM: 1.4, wobbleC: 1.1, open: 2.6 },
  recenter:  { tempo: 1.10, breathRate: 0.82, breathDepth: 5.6, rMembrane: 74, rCore: 53, coreBreath: 3.8, wobbleM: 1.9, wobbleC: 1.55, open: 3.6 },
};

function getIntentConfig(intent) {
  return INTENT_CONFIG[intent] || INTENT_CONFIG.relax;
}

/* La bulle reste disponible à chaque retour jusqu’au clic sur Auria. */
const CHIP_FREE_MIN_SESSIONS = 2;

function chipStateKey(uid, isPremium) {
  return `auriaChip:${uid || "anon"}:${isPremium ? "premium" : "free"}`;
}

function readChipState(uid, isPremium) {
  try {
    const raw = localStorage.getItem(chipStateKey(uid, isPremium));
    if (!raw) return { shows: 0, opened: false, lastShownISO: "" };
    const parsed = JSON.parse(raw);
    return { shows: Number(parsed.shows) || 0, opened: Boolean(parsed.opened), lastShownISO: String(parsed.lastShownISO || "") };
  } catch {
    return { shows: 0, opened: false, lastShownISO: "" };
  }
}

function readSessionCount(uid) {
  try {
    return Number(localStorage.getItem(`sessions:total:${uid}`)) || 0;
  } catch {
    return 0;
  }
}

function writeChipState(uid, isPremium, state) {
  try {
    localStorage.setItem(chipStateKey(uid, isPremium), JSON.stringify(state));
  } catch {
    /* noop */
  }
}



const MOTION = {
  relax: { ampX: 3.2, ampY: 1.8, speed: 1.0 },
  rest: { ampX: 1.1, ampY: 2.0, speed: 0.62 },
  recenter:  { ampX: 2.3, ampY: 2.1, speed: 1.85 },
};

const clampNum = (v, a, b) => Math.max(a, Math.min(b, v));

const AURIA_RING_CIRCUMFERENCE = 628;

function clamp01(v) {
  return clampNum(v, 0, 1);
}

function lerpNum(start, end, t) {
  return start + (end - start) * t;
}

function smoothstep01(t) {
  const p = clamp01(t);
  return p * p * (3 - 2 * p);
}

function getAuriaBreathScale(phase, progress, introPhase = false) {
  const p = clamp01(progress);

  if (phase === "inhale") {
    return lerpNum(introPhase ? 1 : 0.82, 1.32, smoothstep01(p));
  }

  if (phase === "exhale") {
    return lerpNum(1.32, 0.78, smoothstep01(p));
  }

  if (phase === "hold") {
    return lerpNum(1.30, 1.24, Math.sin(p * Math.PI));
  }

  return 1;
}

/** Respiration asymétrique : dwell aux extrêmes, sans cassure au passage par zéro. */
function breathe(t, rate, depth) {
  const s = Math.sin(t * rate);
  const k = 0.62;
  return ((1 + k) * s) / (1 + k * Math.abs(s)) * depth;
}

/** Forme ronde organique fermée (faible wobble = presque un cercle qui respire). */
function makeBlobPath({
  t,
  cx = 120,
  cy = 120,
  radius = 56,
  wobble = 1.6,
  points = 28,
  phase = 0,
  leanX = 0,
  leanY = 0,
  squashY = 1,
}) {
  const pts = [];
  for (let i = 0; i < points; i += 1) {
    const a = (Math.PI * 2 * i) / points;
    const n =
      Math.sin(t * 0.8 + i * 0.8 + phase) * wobble +
      Math.sin(t * 0.42 + i * 1.6 + phase) * wobble * 0.5 +
      Math.cos(t * 0.57 + i * 2.2 + phase) * wobble * 0.32;
    const r = radius + n;
    pts.push({
      x: cx + leanX + Math.cos(a) * r,
      y: cy + leanY + Math.sin(a) * r * squashY,
    });
  }

  let d = `M ${pts[0].x.toFixed(2)} ${pts[0].y.toFixed(2)}`;
  for (let i = 0; i < points; i += 1) {
    const p0 = pts[(i - 1 + points) % points];
    const p1 = pts[i];
    const p2 = pts[(i + 1) % points];
    const p3 = pts[(i + 2) % points];
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${c1x.toFixed(2)} ${c1y.toFixed(2)}, ${c2x.toFixed(2)} ${c2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
  }
  return `${d} Z`;
}

const LivingAIBeing = memo(function LivingAIBeing({
  intent = "relax",
  tone = "warm",
  tod = "evening",
  composing = false,
  breathingActive = false,
  breathPhase = "idle",
  phaseDuration = 3600,
  frozen = false,
  introPhase = false,
  interactive = false,
  afterglow = false,
  breathPeriodMs = 0,
  label = "",
  onClick,
}) {
  const gid = useId().replace(/:/g, "");
  const cfg = getIntentConfig(intent);

  const rootRef = useRef(null);
  const membraneRef = useRef(null);
  const coreRef = useRef(null);
  const glowRef = useRef(null);       // lueur interne diffuse (l'attention)
  const coreGradRef = useRef(null);   // focal du dégradé du noyau

  const [touched, setTouched] = useState(false);
  const [recognizing, setRecognizing] = useState(false);
  const touchTimerRef = useRef(null);
  const recognitionTimerRef = useRef(null);
  const recognitionCooldownRef = useRef(0);
  const sighRef = useRef({ until: 0 });

  const triggerRecognition = useCallback(() => {
    if (!interactive) return;

    const now = performance.now();

    // Évite qu’Auria réagisse continuellement au moindre mouvement.
    if (now < recognitionCooldownRef.current) return;

    recognitionCooldownRef.current = now + 12000;

    // La reconnaissance se termine par une expiration lente.
    sighRef.current.until = Math.max(
      sighRef.current.until,
      now + 2800,
    );

    setRecognizing(true);

    window.clearTimeout(recognitionTimerRef.current);

    recognitionTimerRef.current = window.setTimeout(() => {
      setRecognizing(false);
    }, 2600);
  }, [interactive]);

  // Après une séance, Auria accueille spontanément l’utilisateur.
  useEffect(() => {
    if (!interactive || !afterglow) return undefined;

    const timerId = window.setTimeout(() => {
      triggerRecognition();
    }, 650);

    return () => window.clearTimeout(timerId);
  }, [interactive, afterglow, triggerRecognition]);

  // Soupir autonome : toutes les 26 à 42 s, une grande respiration spontanée
  useEffect(() => {
    let cancelled = false;
    let timerId = 0;
    const schedule = () => {
      timerId = window.setTimeout(() => {
        if (cancelled) return;
        sighRef.current.until = performance.now() + 4600;
        schedule();
      }, 26000 + Math.random() * 16000);
    };
    schedule();
    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, []);

const handleBeingTouch = useCallback(() => {
  if (!interactive) return;
  triggerRecognition();
  const st = stateRef.current;
  st.pulse.start = performance.now();
  st.pulse.next = performance.now() + 5200;
  sighRef.current.until = performance.now() + 4600;
  setTouched(true);
  window.clearTimeout(touchTimerRef.current);
  touchTimerRef.current = window.setTimeout(() => setTouched(false), 1700);
}, [interactive, triggerRecognition]);

useEffect(() => {
  return () => {
    window.clearTimeout(touchTimerRef.current);
    window.clearTimeout(recognitionTimerRef.current);
  };
}, []);

const stateRef = useRef({
  phase: 0,
  bPhase: 0,     // phase de respiration accumulée
  last: 0,
  cur: 0,
  sighAmt: 0,
  doze: 0,       // niveau d'assoupissement (0 = éveillée)
  awakeAt: 0,    // dernier instant de présence
  driftMean: { x: 0, y: 0 },
  lean: { x: 0, y: 0 },
  gaze: { x: 0, y: 0, tx: 0, ty: 0, idleNext: 0 },
  pulse: { next: 0, start: -999 },
  blink: { next: 0, start: -999 },
  wake: 0,
  lastGlow: -1,
  lastCur: -1,
});

  const cfgRef = useRef(cfg);
  cfgRef.current = cfg;
  const breathPeriodRef = useRef(breathPeriodMs);
  breathPeriodRef.current = breathPeriodMs;
  const composingRef = useRef(composing);
  composingRef.current = composing;
  const frozenRef = useRef(frozen);
  frozenRef.current = frozen;

  const breathVisualRef = useRef({
  active: false,
  phase: "idle",
  duration: 3600,
  intro: false,
  startedAt: 0,
  lastScale: 1,
});

useEffect(() => {
  const b = breathVisualRef.current;
  const nextPhase = breathPhase || "idle";
  const nextDuration = Math.max(1, Number(phaseDuration) || 3600);
  const now = performance.now();

  const hasChanged =
    b.active !== breathingActive ||
    b.phase !== nextPhase ||
    b.duration !== nextDuration ||
    b.intro !== introPhase;

  if (!hasChanged) return;

  b.active = breathingActive;
  b.phase = nextPhase;
  b.duration = nextDuration;
  b.intro = introPhase;
  b.startedAt = now;

  if (!breathingActive && rootRef.current) {
    rootRef.current.style.setProperty("--aic-breath-progress", "0");
    rootRef.current.style.setProperty("--aic-breath-scale", "1");
    rootRef.current.style.setProperty("--aic-ring-dash", `${AURIA_RING_CIRCUMFERENCE}`);
  }
}, [breathingActive, breathPhase, phaseDuration, introPhase]);

  useEffect(() => {
    const root = rootRef.current;
    const membrane = membraneRef.current;
    const core = coreRef.current;
    const glow = glowRef.current;
    const grad = coreGradRef.current;
    if (!root || !membrane || !core || !glow) return;

    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

    if (reduce) {
      const c = cfgRef.current;
      membrane.setAttribute("d", makeBlobPath({ t: 0, radius: c.rMembrane, wobble: c.wobbleM }));
      core.setAttribute("d", makeBlobPath({ t: 1.7, phase: 1.7, radius: c.rCore, wobble: c.wobbleC }));
      glow.setAttribute("transform", "translate(0,0)");
      root.style.setProperty("--aic-being-glow", "0.6");
      return;
    }
    
    let rafId = 0;
    let mounted = true;
    const s = stateRef.current;
    const mo = MOTION[intent] || MOTION.relax;
    const loop = (now) => {
      if (!mounted) return;
      const c = cfgRef.current;
      const isComposing = composingRef.current;

      const dt = s.last ? Math.min(0.05, (now - s.last) / 1000) : 0;
      s.last = now;

      // Soupir : amplitude lissée (aucun saut visuel à l'entrée/sortie)
      const sighTarget = now < sighRef.current.until ? 1 : 0;
      s.sighAmt = (s.sighAmt || 0) + (sighTarget - (s.sighAmt || 0)) * Math.min(1, dt * 2.2);
      const sigh = s.sighAmt;

      const cur = 0;

      const tempo =
        c.tempo *
        (isComposing ? 1.18 : 1) *
        (1 - sigh * 0.28) *
        (1 - s.doze * 0.3);

      s.phase += dt * tempo;
      const t = s.phase;

      // Aucune respiration n'est identique : deux ondes lentes non multiples.
      const vr = 1 + Math.sin(t * 0.083) * 0.13 + Math.sin(t * 0.037 + 2.1) * 0.09;
      const vd = 1 + Math.sin(t * 0.061 + 1.3) * 0.16 + Math.sin(t * 0.029 + 0.4) * 0.1;

      // breathPeriodMs impose un cycle fixe (10 000 ms = 5 s / 5 s),
      // légèrement modulé pour rester organique (jamais métronomique).
      const fixedPeriod = breathPeriodRef.current;
      const breathAdvance = fixedPeriod
        ? ((Math.PI * 2 * 1000) / fixedPeriod) *
          (1 + (vr - 1) * 0.3) *
          (1 - sigh * 0.22)
        : tempo * c.breathRate * vr;
      s.bPhase = (s.bPhase || 0) + dt * breathAdvance;
      const bp = s.bPhase;

      // Respirations (plus amples et plus lentes pendant un soupir)
      const breathM = breathe(bp, 1, c.breathDepth * vd * (1 + sigh * 0.35));
      const breathC = breathe(bp * 1.06, 1, c.coreBreath * vd * (1 + sigh * 0.25));

      // "pensée" : pulsation douce périodique
      if (now > s.pulse.next) {
        s.pulse.start = now;
        s.pulse.next = now + (isComposing ? 1500 : 2700) + Math.random() * 2100;
      }
      const pulseAge = now - s.pulse.start;
      const pulse = pulseAge < 380 ? Math.sin((pulseAge / 380) * Math.PI) : 0;

            // Clignement : la lueur d'attention baisse brièvement, comme un battement de paupières.
      if (now > s.blink.next) {
        s.blink.start = now;
        s.blink.next = now + 3800 + Math.random() * 5200;
      }
      const blinkAge = now - s.blink.start;
      const blink = blinkAge < 260 ? Math.sin((blinkAge / 260) * Math.PI) : 0;

// dérive autonome — caractère selon l'objectif (jamais de boucle : sinus non multiples)
const wt = t * mo.speed;
let wanderX =
  (Math.sin(wt * 0.13) * 1.8 + Math.sin(wt * 0.07 + 1.5) * 1.2) * mo.ampX;
let wanderY =
  (Math.cos(wt * 0.11 + 0.6) * 4 + Math.sin(wt * 0.05 + 2.2) * 2) * mo.ampY;

if (intent === "rest") {
  // Repos : dodelinement vertical lent et marqué (la tête qui pique)
  wanderY += Math.max(0, Math.sin(wt * 0.04)) * 7;
} else if (intent === "recenter") {
  // Recentrage : impulsions vives et nerveuses superposées
  wanderX += Math.sin(wt * 0.5) * 2.2 + Math.sin(wt * 0.9 + 1) * 1;
  wanderY += Math.cos(wt * 0.42 + 0.5) * 1.8;
} else if (intent === "relax") {
  // Vagues : une houle lente et large par-dessus le balancement
  wanderX += Math.sin(wt * 0.045) * 2.4;
}

      // Centre de gravité : on retire la dérive lente pour qu'Auria oscille
      // AUTOUR du centre au lieu de s'en éloigner durablement.
      if (!s.driftMean) s.driftMean = { x: 0, y: 0 };
      s.driftMean.x += (wanderX - s.driftMean.x) * Math.min(1, dt * 0.1);
      s.driftMean.y += (wanderY - s.driftMean.y) * Math.min(1, dt * 0.1);
      wanderX -= s.driftMean.x;
      wanderY -= s.driftMean.y;

      // Saturation douce : l'amplitude s'approche de 12 px sans jamais buter.
      const driftR = Math.hypot(wanderX, wanderY);
      if (driftR > 0.001) {
        const soft = (12 * Math.tanh(driftR / 12)) / driftR;
        wanderX *= soft;
        wanderY *= soft;
      }

      const g = s.gaze;
      if (now > g.idleNext) {
        g.tx = (Math.random() * 2 - 1) * 9;
        g.ty = (Math.random() * 2 - 1) * 7;
        g.idleNext = now + 1500 + Math.random() * 2200;
      }
      g.x += (g.tx - g.x) * 0.06;
      g.y += (g.ty - g.y) * 0.06;

      // membrane (halo doux rond) : s'ouvre au hover, se contracte en concentration
      membrane.setAttribute(
        "d",
        makeBlobPath({
          t,
          radius: c.rMembrane + breathM + c.open * cur - (isComposing ? 2 : 0),
          wobble: c.wobbleM,
          leanX: s.lean.x * 0.4,
          leanY: s.lean.y * 0.4,
          squashY: 1 + breathM * 0.014,
        })
      );

      // noyau rond, vivant
      core.setAttribute(
        "d",
        makeBlobPath({
          t: t * 1.2,
          phase: 1.7,
          radius: c.rCore + breathC * 0.65 + (isComposing ? 1.2 : 0) - pulse,
          wobble: c.wobbleC + (isComposing ? 0.35 : 0),
          leanX: s.lean.x * 0.8,
          leanY: s.lean.y * 0.8,
        })
      );

      // lueur interne diffuse = l'attention (translate vers le regard, sans arête)
      glow.setAttribute("transform", `translate(${g.x.toFixed(2)},${g.y.toFixed(2)})`);

      // le foyer lumineux du dégradé du noyau s'oriente vers l'utilisateur
      if (grad) {
        const fx = clampNum(0.42 + (g.x / 30), 0.26, 0.6);
        const fy = clampNum(0.34 + (g.y / 30), 0.2, 0.55);
        grad.setAttribute("fx", fx.toFixed(3));
        grad.setAttribute("fy", fy.toFixed(3));
      }

// Respiration AURIA pilotée au RAF, comme les autres breathing animations.
// On évite les keyframes CSS qui redémarrent à chaque phase.
const b = breathVisualRef.current;
let breathProgress = 0;
let breathScale = b.lastScale || 1;

if (b.active && b.phase !== "idle") {
  const duration = Math.max(1, b.duration || 3600);
  breathProgress = clamp01((now - b.startedAt) / duration);
  breathScale = getAuriaBreathScale(b.phase, breathProgress, b.intro);
  b.lastScale = breathScale;
} else {
  b.lastScale += (1 - b.lastScale) * Math.min(1, dt * 8);
  breathScale = b.lastScale;
}

root.style.setProperty("--aic-breath-progress", breathProgress.toFixed(4));
root.style.setProperty("--aic-breath-scale", breathScale.toFixed(4));
root.style.setProperty(
  "--aic-ring-dash",
  (AURIA_RING_CIRCUMFERENCE * (1 - breathProgress)).toFixed(2)
);

const glowI = clampNum(
  0.42 + cur * 0.4 + (isComposing ? 0.28 : 0) + pulse * 0.3 - s.doze * 0.2 - blink * 0.45,
  0,
  1,
);

// Les custom properties invalident tout le sous-arbre : on n'écrit que si ça change vraiment.
if (Math.abs(glowI - s.lastGlow) > 0.004) {
  s.lastGlow = glowI;
  root.style.setProperty("--aic-being-glow", glowI.toFixed(3));
}

if (Math.abs(cur - s.lastCur) > 0.004) {
  s.lastCur = cur;
  root.style.setProperty("--aic-being-curiosity", cur.toFixed(3));
}

const idleBreathScale = 1 + breathM * 0.005;
let driftX = 0;
let driftY = 0;

if (frozenRef.current) {
  // Figé au centre : la reprise repartira du milieu.
  s.wake = 0;
} else {
  // Rampe d'éveil : l'amplitude monte de 0 → 1 en douceur (~1,7 s).
  s.wake = Math.min(1, (s.wake || 0) + dt * 0.6);
  driftX = wanderX * s.wake;
  driftY = wanderY * s.wake;
}

// Un seul transform composité par frame, au lieu de quatre variables CSS.
root.style.transform = `translate3d(${driftX.toFixed(2)}px, ${driftY.toFixed(2)}px, 0) scale(${idleBreathScale.toFixed(4)})`;

      rafId = window.requestAnimationFrame(loop);
    };

    rafId = window.requestAnimationFrame(loop);
    return () => {
      mounted = false;
      window.cancelAnimationFrame(rafId);
    };
  }, [intent]);

  const onKeyDown = useCallback(
    (e) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();
      onClick?.();
    },
    [onClick]
  );

return (
  <div
    ref={rootRef}
    className="aic-living-being"
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    data-intent={intent}
    data-tone={tone}
    data-tod={tod}
    data-composing={composing ? "true" : "false"}
    data-breathing={breathingActive ? "true" : "false"}
    data-phase={breathPhase || "idle"}
    data-intro={introPhase ? "true" : "false"}
    data-touched={touched ? "true" : "false"}
    data-recognizing={recognizing ? "true" : "false"}
    data-afterglow={afterglow ? "true" : "false"}
    onPointerDown={interactive ? handleBeingTouch : undefined}
style={{
  "--aic-phase-duration": `${phaseDuration}ms`,
  "--aic-breath-progress": "0",
  "--aic-breath-scale": "1",
  "--aic-ring-dash": `${AURIA_RING_CIRCUMFERENCE}`,
}}
    aria-label={
      onClick
        ? label ||
          (breathingActive
            ? "Auria guide votre respiration"
            : "Démarrer une respiration avec Auria")
        : undefined
    }
    aria-pressed={onClick ? breathingActive : undefined}
    onClick={onClick}
    onKeyDown={onClick ? onKeyDown : undefined}
  >


      <svg className="aic-living-being__svg" viewBox="0 0 240 240" role="img" aria-hidden="true">
        <defs>
          <radialGradient id={`bm-${gid}`} cx="46%" cy="44%" r="68%">
            <stop offset="0%" stopColor="var(--being-m0)" />
            <stop offset="100%" stopColor="var(--being-m1)" />
          </radialGradient>
          <radialGradient ref={coreGradRef} id={`bc-${gid}`} cx="42%" cy="34%" fx="42%" fy="34%" r="76%">
            <stop offset="0%" stopColor="var(--being-c0)" />
            <stop offset="42%" stopColor="var(--being-c1)" />
            <stop offset="76%" stopColor="var(--being-c2)" />
            <stop offset="100%" stopColor="var(--being-c3)" />
          </radialGradient>
          <radialGradient id={`bi-${gid}`} cx="50%" cy="50%" r="55%">
            <stop offset="0%" stopColor="var(--being-iris0)" />
            <stop offset="55%" stopColor="var(--being-iris1)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </radialGradient>
          <filter id={`bsoft-${gid}`} x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="8" />
          </filter>
          <filter id={`bmem-${gid}`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="7" />
          </filter>
        </defs>

        {/* Halo membrane — rond, très doux */}
        <path
          ref={membraneRef}
          className="aic-living-being__membrane"
          d={makeBlobPath({ t: 0, radius: cfg.rMembrane, wobble: cfg.wobbleM })}
          fill={`url(#bm-${gid})`}
          filter={`url(#bmem-${gid})`}
        />

        {/* Noyau rond, vivant */}
        <path
          ref={coreRef}
          className="aic-living-being__core"
          d={makeBlobPath({ t: 1.7, phase: 1.7, radius: cfg.rCore, wobble: cfg.wobbleC })}
          fill={`url(#bc-${gid})`}
        />

        {/* Arc de concentration (composing) */}
        <circle className="aic-living-being__scan" cx="120" cy="120" r="68" />
        <circle className="aic-living-being__ring-track" cx="120" cy="120" r="100" />
        <circle className="aic-living-being__ring" cx="120" cy="120" r="100" />

        {/* Lueur interne diffuse = l'attention (aucune arête) */}
        <g ref={glowRef} className="aic-living-being__attn">
          <circle
            className="aic-living-being__attn-glow"
            cx="120"
            cy="116"
            r="26"
            fill={`url(#bi-${gid})`}
            filter={`url(#bsoft-${gid})`}
          />
        </g>
      </svg>
    </div>
  );
});


function AIComposerCard({ uid, isPremium = false, chipReady = false, preferredIntent = "", onStartMoment, onStartCustomMoment, onOpenRefuge, onLockedClick, onOpenChange }) {
  const navigate = useNavigate();
  const [auriaAfterglow, setAuriaAfterglow] = useState(false);
  const [freeRefugeOpen, setFreeRefugeOpen] = useState(false);
  const [chipVisible, setChipVisible] = useState(false);
  const visualIntent = VALID_INTENTS.has(preferredIntent) ? preferredIntent : "relax";
  const contextMessage = AURIA_CONTEXT_MESSAGES[visualIntent];

  const localTod = useMemo(() => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 11) return "morning";
    if (hour >= 11 && hour < 17) return "day";
    if (hour >= 17 && hour < 22) return "evening";
    return "night";
  }, []);

  useEffect(() => {
    onOpenChange?.(freeRefugeOpen);
  }, [freeRefugeOpen, onOpenChange]);

  useEffect(() => {
  if (!freeRefugeOpen) return undefined;

  const handleOutsidePointerDown = (event) => {
    const target = event.target;

    if (!(target instanceof Element)) return;
    if (target.closest(".aic-home-ritual__free-refuge")) return;
    if (target.closest(".aic-home-ritual__presence .aic-living-being")) return;

    setFreeRefugeOpen(false);
  };

  document.addEventListener("pointerdown", handleOutsidePointerDown);

  return () => {
    document.removeEventListener("pointerdown", handleOutsidePointerDown);
  };
}, [freeRefugeOpen]);

  useEffect(() => {
    try {
      const until = Number(sessionStorage.getItem("auria:afterglow") || 0);
      setAuriaAfterglow(Date.now() < until);
    } catch {
      setAuriaAfterglow(false);
    }
  }, []);

useEffect(() => {
  const eligible = Boolean(uid && chipReady && (isPremium || readSessionCount(uid) >= CHIP_FREE_MIN_SESSIONS));
  setChipVisible(eligible && !readChipState(uid, isPremium).opened);
}, [uid, isPremium, chipReady]);

  const openMomentPage = useCallback(() => {
    const start = onStartMoment || onStartCustomMoment;

    if (start) {
      start();
      return;
    }

    navigate("/moment", { state: { from: "/" } });
  }, [navigate, onStartCustomMoment, onStartMoment]);

const handleStart = useCallback(() => {
  setFreeRefugeOpen(false);
  openMomentPage();
}, [openMomentPage]);

const handleAuriaOpen = useCallback(() => {
  setChipVisible(false);
  writeChipState(uid, isPremium, { ...readChipState(uid, isPremium), opened: true });

  if (isPremium) {
    setFreeRefugeOpen(false);
    if (onOpenRefuge) {
      onOpenRefuge();
      return;
    }
    navigate("/refuge", { state: { from: "/" } });
    return;
  }

  setFreeRefugeOpen((current) => !current);
}, [isPremium, navigate, onOpenRefuge, uid]);

  const handleDiscoverRefuge = useCallback(() => {
    setFreeRefugeOpen(false);
    onLockedClick?.("auria-refuge");
  }, [onLockedClick]);

  return (
    <section
      className={`aic-card aic-home-ritual ${freeRefugeOpen ? "is-free-refuge-open" : ""}`}
      aria-labelledby="auria-home-title"
    >
      <div className="aic-card__inner aic-home-ritual__inner">
        <div className="aic-home-ritual__center">
          <p className="aic-home-ritual__eyebrow">Votre espace de respiration</p>
          <h2
            id="auria-home-title"
            className="aic-home-ritual__title"
          >
            {contextMessage}
          </h2>

          <div className="aic-home-ritual__focus">
<div
  className={`aic-home-ritual__auria-hint ${chipVisible ? "" : "is-hidden"}`}
  aria-hidden={chipVisible ? undefined : "true"}
>
  {isPremium ? "Créer une session avec Auria" : "Découvrir le Refuge Personnel"}
</div>

            <div className="aic-home-ritual__presence">
              <span className="aic-home-ritual__water-rest" aria-hidden="true">
                <span className="aic-home-ritual__water-rest-line aic-home-ritual__water-rest-line--1" />
                <span className="aic-home-ritual__water-rest-line aic-home-ritual__water-rest-line--2" />
              </span>
{!isPremium && freeRefugeOpen && (
  <button
    type="button"
    className="aic-home-ritual__free-refuge"
    onClick={handleDiscoverRefuge}
    aria-label="Découvrir mon Refuge Personnel"
  >
    <span className="aic-home-ritual__free-refuge-copy">
      <strong>Et si Auria s’adaptait vraiment à vous ?</strong>
      <span className="aic-home-ritual__free-refuge-text">Votre ressenti suffit. Auria prépare une session pensée pour votre moment.</span>
    </span>

    <span className="aic-home-ritual__free-refuge-cta">
      Découvrir mon Refuge Personnel
      <ArrowRight size={16} strokeWidth={2.3} aria-hidden="true" />
    </span>
  </button>
)}
              <LivingAIBeing
                intent={visualIntent}
                tone="warm"
                composing={false}
                breathingActive={false}
                breathPhase="idle"
                phaseDuration={3600}
                breathPeriodMs={11000}
                frozen={false}
                introPhase={false}
                interactive={true}
                afterglow={auriaAfterglow}
                tod={localTod}
                label={isPremium ? "Ouvrir mon Refuge Personnel" : "Découvrir ce qu’Auria peut faire avec le Refuge Personnel"}
                onClick={handleAuriaOpen}
              />
            </div>

            <div className="aic-home-ritual__actions">
              <button
                type="button"
                className="aic-home-ritual__primary"
                onClick={handleStart}
              >
                <span className="aic-home-ritual__primary-copy">
                  <strong>Commencer mon moment</strong>
                  <small>Respirations guidées</small>
                </span>
                <ArrowRight className="aic-home-ritual__primary-arrow" size={21} strokeWidth={2.2} aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export { LivingAIBeing };
export default AIComposerCard;
